import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from '../../utils/i18n';
import axios from 'axios';
import {
  Code,
  FileCode,
  AlertTriangle,
  Lock,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Shield,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Custom Code Settings Page - Phase 9.1
 * 
 * Reserved structure for Custom CSS and JavaScript.
 * Platform owner only access.
 * Disabled by default for safety.
 */
const CustomCodeSettings = () => {
  const { user, token } = useAuth();
  const { language } = useTheme();
  const { t } = useTranslation(language);
  
  const [customCSS, setCustomCSS] = useState('');
  const [customJS, setCustomJS] = useState('');
  const [cssEnabled, setCSSEnabled] = useState(false);
  const [jsEnabled, setJSEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Only platform owners can access this
  const isPlatformOwner = ['platform_owner', 'super_admin'].includes(user?.role);
  const canEditCustomCode = isPlatformOwner;

  useEffect(() => {
    if (canEditCustomCode) {
      loadCustomCode();
    }
  }, [canEditCustomCode]);

  const loadCustomCode = async () => {
    try {
      const response = await axios.get(`${API}/settings/custom-code`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomCSS(response.data.custom_css || '');
      setCustomJS(response.data.custom_js || '');
      setCSSEnabled(response.data.css_enabled || false);
      setJSEnabled(response.data.js_enabled || false);
    } catch (err) {
      // If endpoint doesn't exist yet, use defaults
      console.log('Custom code endpoint not available yet');
    }
  };

  const saveCustomCode = async () => {
    setIsSaving(true);
    setMessage(null);
    
    try {
      await axios.post(`${API}/settings/custom-code`, {
        custom_css: customCSS,
        custom_js: customJS,
        css_enabled: cssEnabled,
        js_enabled: jsEnabled,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: 'success', text: 'Custom code saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save custom code. Backend endpoint may not be available yet.' });
    } finally {
      setIsSaving(false);
    }
  };

  const resetCode = () => {
    setCustomCSS('');
    setCustomJS('');
    setCSSEnabled(false);
    setJSEnabled(false);
    setMessage({ type: 'info', text: 'Reset to defaults. Click Save to apply.' });
  };

  if (!isPlatformOwner) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="custom-code-restricted">
        <div className="bg-slate-100 dark:bg-stone-800 rounded-xl p-8 text-center">
          <Lock className="mx-auto text-slate-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
            {t('customCode.platformOnly')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Custom code settings are only available for Platform Owners.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="custom-code-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-900 dark:text-white flex items-center gap-3">
          <Code className="text-emerald-500" size={28} />
          {t('customCode.title')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('customCode.subtitle')}
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Warning:</strong> {t('customCode.warning')}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
            Custom code execution is currently disabled for safety. Settings are stored but not executed.
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`rounded-xl p-4 ${
          message.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' :
          message.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
          'bg-slate-100 dark:bg-stone-800 text-slate-700 dark:text-slate-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Custom CSS */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <FileCode className="text-amber-600 dark:text-amber-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
                {t('customCode.css')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('customCode.cssDesc')}
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-slate-500">
              {cssEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={cssEnabled}
                onChange={(e) => setCSSEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${cssEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${cssEnabled ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
              </div>
            </div>
          </label>
        </div>
        
        <textarea
          value={customCSS}
          onChange={(e) => setCustomCSS(e.target.value)}
          placeholder="/* Add your custom CSS here */
.my-custom-class {
  color: #10b981;
}"
          className="w-full h-48 p-4 font-mono text-sm bg-slate-50 dark:bg-stone-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
          data-testid="custom-css-textarea"
        />
      </section>

      {/* Custom JavaScript */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Code className="text-amber-600 dark:text-amber-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
                {t('customCode.js')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('customCode.jsDesc')}
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-slate-500">
              {jsEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={jsEnabled}
                onChange={(e) => setJSEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${jsEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${jsEnabled ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
              </div>
            </div>
          </label>
        </div>
        
        <textarea
          value={customJS}
          onChange={(e) => setCustomJS(e.target.value)}
          placeholder="// Add your custom JavaScript here
// Note: Execution is currently disabled
console.log('FundElite custom script loaded');"
          className="w-full h-48 p-4 font-mono text-sm bg-slate-50 dark:bg-stone-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
          data-testid="custom-js-textarea"
        />
      </section>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={resetCode}
          className="gap-2"
        >
          <RotateCcw size={16} />
          Reset
        </Button>
        <Button
          onClick={saveCustomCode}
          disabled={isSaving}
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
        >
          <Save size={16} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Future Features Notice */}
      <div className="bg-slate-50 dark:bg-stone-800/50 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
        <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
          <Shield size={18} className="text-slate-500" />
          Coming Soon
        </h3>
        <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-2">
          <li>• White-label agency portal customization</li>
          <li>• Investor portal branding options</li>
          <li>• Custom tracking scripts integration</li>
          <li>• Advanced CSS variable overrides</li>
          <li>• Sandbox preview before applying</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomCodeSettings;
